import { Context, Data, Effect, Layer, Schema } from 'effect';
import { Authorization } from '@okrapdf/schemas/http-authorization';
import {
  HttpApi,
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
  OpenApi,
} from 'effect/unstable/httpapi';
import { SelfHostResourceIdSchema } from './self-host-resource-id.js';

export const SelfHostJsonObject = Schema.Record(Schema.String, Schema.Json).annotate({
  identifier: 'JsonObject',
  description: 'A JSON object whose fields are defined by the selected runtime capability.',
});

const DocumentIdParams = {
  id: SelfHostResourceIdSchema.annotate({ description: 'Stable document identifier.' }),
};

const DocumentPageParams = {
  ...DocumentIdParams,
  page: Schema.String.annotate({ description: 'One-based page number.' }),
};

const RunIdParams = {
  id: SelfHostResourceIdSchema.annotate({
    description: 'Stable workflow or capability run identifier.',
  }),
};

export const SelfHostHealthSchema = Schema.Struct({
  object: Schema.Literal('self_host_health'),
  ok: Schema.Boolean,
  schema_version: Schema.optionalKey(Schema.String),
  recipes: Schema.Finite,
  capabilities: Schema.Finite,
  n8n_workflows: Schema.Finite,
}).annotate({
  identifier: 'SelfHostHealth',
  description: 'Liveness and loaded-catalog summary for the self-host runtime.',
});

const SelfHostHealthUnavailable = SelfHostHealthSchema.pipe(HttpApiSchema.status(503)).annotate({
  identifier: 'SelfHostHealthUnavailable',
});

export const SelfHostRuntimeStatusSchema = Schema.Struct({
  object: Schema.Literal('self_host_runtime_status'),
  ok: Schema.Boolean,
  root: Schema.String,
  schemaVersion: Schema.optionalKey(Schema.String),
  uiRuntime: Schema.optionalKey(Schema.String),
  apiRuntime: Schema.optionalKey(Schema.String),
  auth: SelfHostJsonObject,
  deploymentTargets: Schema.Array(Schema.String),
  validation: SelfHostJsonObject,
  services: Schema.Array(SelfHostJsonObject),
  dockerNetworks: Schema.Array(SelfHostJsonObject),
  recipes: Schema.Array(SelfHostJsonObject),
  capabilities: Schema.Array(SelfHostJsonObject),
  n8nWorkflows: Schema.Array(SelfHostJsonObject),
  endpoints: Schema.Array(Schema.String),
}).annotate({
  identifier: 'SelfHostRuntimeStatus',
  description: 'Validated runtime manifest, loaded capabilities, recipes, auth mode, and routes.',
});

const SelfHostRuntimeUnavailable = SelfHostRuntimeStatusSchema.pipe(
  HttpApiSchema.status(503),
).annotate({
  identifier: 'SelfHostRuntimeUnavailable',
});

const WorkflowRecipeStep = Schema.Struct({
  id: Schema.String,
  uses: Schema.String,
  reads: Schema.Array(Schema.String),
  writes: Schema.Array(Schema.String),
  requires_review: Schema.Boolean,
}).annotate({ identifier: 'WorkflowRecipeStep' });

const WorkflowRecipe = Schema.Struct({
  id: Schema.String,
  name: Schema.String,
  version: Schema.optionalKey(Schema.String),
  capability_refs: Schema.Array(Schema.String),
  graph_reads: Schema.Array(Schema.String),
  graph_writes: Schema.Array(Schema.String),
  steps: Schema.Array(WorkflowRecipeStep),
}).annotate({
  identifier: 'WorkflowRecipe',
  description: 'A versioned workflow recipe composed from capability references.',
});

export const SelfHostWorkflowCatalogSchema = Schema.Struct({
  object: Schema.Literal('workflow_catalog'),
  data: Schema.Array(WorkflowRecipe),
}).annotate({ identifier: 'WorkflowCatalog' });

export const SelfHostCapabilityCatalog = Schema.Struct({
  object: Schema.Literal('capability_catalog'),
  data: Schema.Array(SelfHostJsonObject),
}).annotate({ identifier: 'CapabilityCatalog' });

const WorkflowRunRequest = Schema.Struct({
  recipe_id: Schema.optionalKey(Schema.String).annotate({
    description: 'Canonical workflow recipe identifier.',
  }),
  recipeId: Schema.optionalKey(Schema.String).annotate({
    description: 'Compatibility alias for recipe_id.',
  }),
  source_id: Schema.optionalKey(SelfHostResourceIdSchema),
  sourceId: Schema.optionalKey(SelfHostResourceIdSchema),
  document_id: Schema.optionalKey(SelfHostResourceIdSchema),
  documentId: Schema.optionalKey(SelfHostResourceIdSchema),
  parameters: Schema.optionalKey(SelfHostJsonObject).annotate({
    description:
      'Recipe parameters such as OCR strategy, VLM model, gate thresholds, and review policy.',
  }),
}).annotate({
  identifier: 'WorkflowRunRequest',
  description: 'Starts a typed self-host workflow against a known source or document.',
});

const WorkflowResourceCreateRequest = Schema.Struct({
  name: Schema.optionalKey(Schema.String),
  definition: Schema.Struct({
    code: Schema.String,
  }),
  run: Schema.Boolean,
  inputs: Schema.optionalKey(SelfHostJsonObject),
  params: Schema.optionalKey(SelfHostJsonObject),
}).annotate({
  identifier: 'WorkflowResourceCreateRequest',
  description:
    'Canonical workflow resource envelope shared by the SDK, CLI, hosted API, and self-host runtime.',
});

const WorkflowPostRequest = [WorkflowResourceCreateRequest, WorkflowRunRequest] as const;

export const SelfHostWorkflowRunSchema = Schema.Struct({
  object: Schema.Literal('workflow_run'),
  id: Schema.String,
  status: Schema.Literal('succeeded'),
  mode: Schema.Literals(['self_host_starter', 'external_http']),
  recipe_id: Schema.String,
  source_id: Schema.NullOr(Schema.String),
  document_id: Schema.String,
  graph_writes: Schema.Array(Schema.String),
  graph_url: Schema.String,
  review_url: Schema.String,
  message: Schema.String,
  capability_runs: Schema.Array(SelfHostJsonObject),
}).annotate({
  identifier: 'WorkflowRun',
  description: 'Completed starter workflow run and its ordered capability-run evidence.',
});

const WorkflowRunAccepted = SelfHostWorkflowRunSchema.pipe(HttpApiSchema.status(202)).annotate({
  identifier: 'WorkflowRunAccepted',
});

export const SelfHostDynamicWorkflowRunSchema = Schema.Struct({
  object: Schema.Literal('run'),
  id: Schema.String,
  run_id: Schema.String,
  workflow_id: Schema.String,
  status: Schema.Literals(['queued', 'running', 'succeeded', 'failed', 'canceled']),
  status_url: Schema.String,
  output: Schema.optionalKey(SelfHostJsonObject),
  error: Schema.optionalKey(Schema.NullOr(Schema.String)),
  created_at: Schema.String,
  updated_at: Schema.String,
}).annotate({
  identifier: 'DynamicWorkflowRun',
  description: 'A self-host run of the same dynamic workflow definition accepted by the hosted API.',
});

export const SelfHostWorkflowResourceSchema = Schema.Struct({
  object: Schema.Literal('workflow'),
  id: Schema.String,
  workflow_id: Schema.String,
  name: Schema.NullOr(Schema.String),
  version_id: Schema.String,
  execution_mode: Schema.Literal('agent_workflow_script'),
  runnable: Schema.Boolean,
  created_at: Schema.String,
  run: Schema.NullOr(SelfHostDynamicWorkflowRunSchema),
}).annotate({
  identifier: 'WorkflowResource',
  description: 'Canonical workflow resource projection returned by hosted and self-host runtimes.',
});

const WorkflowResourceCreated = SelfHostWorkflowResourceSchema.pipe(
  HttpApiSchema.status(201),
).annotate({ identifier: 'WorkflowResourceCreated' });

export const SelfHostCapabilityRunSchema = Schema.Struct({
  object: Schema.Literal('capability_run'),
  id: Schema.String,
  workflow_run_id: Schema.String,
  recipe_id: Schema.String,
  step_id: Schema.String,
  capability_id: Schema.String,
  status: Schema.Literal('succeeded'),
  mode: Schema.Literals(['self_host_starter', 'external_http']),
  graph_writes: Schema.Array(Schema.String),
  started_at: Schema.String,
  completed_at: Schema.String,
  output: SelfHostJsonObject,
  endpoint_url: Schema.optionalKey(Schema.String),
}).annotate({ identifier: 'CapabilityRun' });

const DocumentGraph = SelfHostJsonObject.annotate({
  identifier: 'OkraDocumentGraph',
  description:
    'Normalized okra-document-graph/v1 state with bbox-cited blocks and workflow lineage.',
});

const DocumentUploadUrlRequest = Schema.Struct({
  url: Schema.String.annotate({ description: 'HTTP(S) URL of a PDF source.' }),
}).annotate({ identifier: 'DocumentUploadUrlRequest' });

const DocumentUploadResult = Schema.Struct({
  phase: Schema.Literal('complete'),
  document_id: Schema.String,
  source_id: Schema.String,
  pagesTotal: Schema.optionalKey(Schema.Finite),
  graph_url: Schema.String,
}).annotate({ identifier: 'DocumentUploadResult' });

const N8nSourceRequest = SelfHostJsonObject.annotate({
  identifier: 'N8nSourceRequest',
  description: 'n8n webhook payload. document_url is retained when present.',
});

const SourceRef = Schema.Struct({
  object: Schema.Literal('source_ref'),
  id: Schema.String,
  kind: Schema.Literals(['local_upload', 'url', 'n8n_webhook']),
  status: Schema.Literal('accepted'),
  received_at: Schema.String,
  filename: Schema.optionalKey(Schema.String),
  uri: Schema.optionalKey(Schema.String),
  content_type: Schema.optionalKey(Schema.String),
  size_bytes: Schema.optionalKey(Schema.Finite),
  sha256: Schema.optionalKey(Schema.String),
  payload_keys: Schema.optionalKey(Schema.Array(Schema.String)),
})
  .pipe(HttpApiSchema.status(202))
  .annotate({ identifier: 'SourceRef' });

const ErrorFields = {
  object: Schema.Literal('error'),
  error: Schema.String,
  code: Schema.optionalKey(Schema.String),
  message: Schema.optionalKey(Schema.String),
  capability_id: Schema.optionalKey(Schema.String),
  successor: Schema.optionalKey(Schema.String),
};

const BadRequest = Schema.Struct(ErrorFields).pipe(HttpApiSchema.status(400)).annotate({
  identifier: 'BadRequest',
});
const NotFound = Schema.Struct(ErrorFields).pipe(HttpApiSchema.status(404)).annotate({
  identifier: 'NotFound',
});
const Gone = HttpApiSchema.WithHeaders(
  Schema.Struct({
    ...ErrorFields,
    code: Schema.Literal('GONE'),
    successor: Schema.String,
  }).pipe(HttpApiSchema.status(410)),
  {
    deprecation: Schema.String,
    link: Schema.String,
  },
).annotate({ identifier: 'Gone' });
const InternalServerError = Schema.Struct(ErrorFields).pipe(HttpApiSchema.status(500)).annotate({
  identifier: 'InternalServerError',
});
const WorkflowBadRequest = Schema.Struct({
  ...ErrorFields,
  code: Schema.Literal('BAD_REQUEST'),
})
  .pipe(HttpApiSchema.status(400))
  .annotate({ identifier: 'WorkflowBadRequest' });
const WorkflowCapabilityDispatchFailed = Schema.Struct({
  ...ErrorFields,
  code: Schema.Literal('CAPABILITY_DISPATCH_FAILED'),
  capability_id: Schema.String,
})
  .pipe(HttpApiSchema.status(502))
  .annotate({
    identifier: 'WorkflowCapabilityDispatchFailed',
  });
const WorkflowInternalServerError = Schema.Struct({
  ...ErrorFields,
  code: Schema.Literal('INTERNAL_SERVER_ERROR'),
})
  .pipe(HttpApiSchema.status(500))
  .annotate({ identifier: 'WorkflowInternalServerError' });
const WorkflowRuntimeUnavailable = Schema.Struct({
  ...ErrorFields,
  code: Schema.Literal('RUNTIME_UNAVAILABLE'),
})
  .pipe(HttpApiSchema.status(503))
  .annotate({ identifier: 'WorkflowRuntimeUnavailable' });

interface DocumentableEndpoint<Self> {
  annotateMerge(annotations: ReturnType<typeof OpenApi.annotations>): Self;
}

function documented<Endpoint extends DocumentableEndpoint<Endpoint>>(
  endpoint: Endpoint,
  summary: string,
  description: string,
): Endpoint {
  return endpoint.annotateMerge(OpenApi.annotations({ summary, description })) as Endpoint;
}

export class SelfHostSystemApi extends HttpApiGroup.make('system', { topLevel: true }).add(
  documented(
    HttpApiEndpoint.get('health', '/health', {
      success: SelfHostHealthSchema,
      error: SelfHostHealthUnavailable,
    }),
    'Check runtime health',
    'Reports whether the runtime bundle is valid and how many recipes and capabilities are loaded.',
  ),
  documented(
    HttpApiEndpoint.get('ready', '/ready', {
      success: SelfHostHealthSchema,
      error: SelfHostHealthUnavailable,
    }),
    'Check runtime readiness',
    'Uses the same bundle-validation signal as /health for container readiness probes.',
  ),
  documented(
    HttpApiEndpoint.get('openapi', '/openapi.json', {
      success: SelfHostJsonObject,
    }),
    'Read the OpenAPI contract',
    'Returns the OpenAPI 3.1 document generated from this Effect HttpApi contract.',
  ),
) {}

export class SelfHostRuntimeApi extends HttpApiGroup.make('runtime').add(
  documented(
    HttpApiEndpoint.get('status', '/v1/self-host/status', {
      success: SelfHostRuntimeStatusSchema,
      error: SelfHostRuntimeUnavailable,
    }),
    'Describe the self-host runtime',
    'Returns the validated manifest, auth posture, routes, recipes, capabilities, services, and networks.',
  ),
  documented(
    HttpApiEndpoint.get('manifest', '/v1/runtime/manifest', {
      success: SelfHostJsonObject,
      error: NotFound,
    }),
    'Read the runtime manifest',
    'Returns the deployed runtime.manifest.json document.',
  ),
  documented(
    HttpApiEndpoint.get('capabilities', '/v1/capabilities', {
      success: SelfHostCapabilityCatalog,
    }),
    'List capabilities',
    'Lists installed parser, OCR, VLM, audit, redaction, review, viewer, and integration capabilities.',
  ),
) {}

export class SelfHostWorkflowCatalogApi extends HttpApiGroup.make('workflow-catalog').add(
  documented(
    HttpApiEndpoint.get('catalog', '/v1/workflows/catalog', {
      success: SelfHostWorkflowCatalogSchema,
    }),
    'List workflow recipes',
    'Lists the versioned workflow definitions available to the self-host runtime.',
  ),
  documented(
    HttpApiEndpoint.get('deprecatedRecipes', '/v1/recipes', {
      success: Schema.Never,
      error: Gone,
    }),
    'Deprecated workflow catalog alias',
    'Always returns 410 Gone and points callers at /v1/workflows/catalog.',
  ),
) {}

export class SelfHostWorkflowApi extends HttpApiGroup.make('workflows').add(
  documented(
    HttpApiEndpoint.post('run', '/v1/workflows', {
      payload: WorkflowPostRequest,
      success: [WorkflowRunAccepted, WorkflowResourceCreated],
      error: [
        WorkflowBadRequest,
        WorkflowCapabilityDispatchFailed,
        WorkflowRuntimeUnavailable,
        WorkflowInternalServerError,
      ],
    }).middleware(Authorization),
    'Run a workflow',
    'Accepts the canonical workflow resource envelope or executes an installed recipe against one document graph.',
  ),
  documented(
    HttpApiEndpoint.get('getRun', '/v1/runs/:id', {
      params: RunIdParams,
      success: [SelfHostWorkflowRunSchema, SelfHostDynamicWorkflowRunSchema],
      error: NotFound,
    }),
    'Read a workflow run',
    'Returns one persisted workflow run with its ordered capability evidence.',
  ),
  documented(
    HttpApiEndpoint.get('getCapabilityRun', '/v1/capability-runs/:id', {
      params: RunIdParams,
      success: SelfHostCapabilityRunSchema,
      error: NotFound,
    }),
    'Read a capability run',
    'Returns one persisted capability step run, including graph writes and endpoint provenance.',
  ),
) {}

export class SelfHostDocumentApi extends HttpApiGroup.make('documents').add(
  documented(
    HttpApiEndpoint.get('graph', '/v1/documents/:id/graph', {
      params: DocumentIdParams,
      success: DocumentGraph,
      error: NotFound,
    }),
    'Read the normalized document graph',
    'Returns bbox-cited blocks, artifacts, findings, reviews, redactions, and workflow lineage.',
  ),
  documented(
    HttpApiEndpoint.get('markdown', '/v1/documents/:id/full.md', {
      params: DocumentIdParams,
      success: Schema.String.pipe(
        HttpApiSchema.asText({ contentType: 'text/markdown; charset=utf-8' }),
      ),
      error: NotFound,
    }),
    'Read document Markdown',
    'Projects the normalized graph into a Markdown representation.',
  ),
  documented(
    HttpApiEndpoint.get('status', '/v1/documents/:id/status', {
      params: DocumentIdParams,
      success: SelfHostJsonObject,
      error: NotFound,
    }),
    'Read document status',
    'Returns parse coverage, node counts, and graph artifact links.',
  ),
  documented(
    HttpApiEndpoint.get('pages', '/v1/documents/:id/pages', {
      params: DocumentIdParams,
      success: Schema.Array(SelfHostJsonObject),
      error: NotFound,
    }),
    'List document pages',
    'Returns page-scoped text and normalized blocks.',
  ),
  documented(
    HttpApiEndpoint.get('nodes', '/v1/documents/:id/nodes', {
      params: DocumentIdParams,
      success: SelfHostJsonObject,
      error: NotFound,
    }),
    'List document nodes',
    'Returns normalized graph nodes with page and bbox evidence.',
  ),
  documented(
    HttpApiEndpoint.get('reviews', '/v1/documents/:id/reviews', {
      params: DocumentIdParams,
      success: SelfHostJsonObject,
      error: NotFound,
    }),
    'List document reviews',
    'Returns human-review gates opened by workflow capabilities.',
  ),
  documented(
    HttpApiEndpoint.get('findings', '/v1/documents/:id/findings', {
      params: DocumentIdParams,
      success: SelfHostJsonObject,
      error: NotFound,
    }),
    'List document findings',
    'Returns audit or extraction findings attached to the document graph.',
  ),
  documented(
    HttpApiEndpoint.get('redactions', '/v1/documents/:id/redactions', {
      params: DocumentIdParams,
      success: SelfHostJsonObject,
      error: NotFound,
    }),
    'List document redactions',
    'Returns proposed or applied redactions attached to the document graph.',
  ),
) {}

export class SelfHostSourceApi extends HttpApiGroup.make('sources').add(
  documented(
    HttpApiEndpoint.post('fromN8n', '/v1/sources/n8n', {
      payload: N8nSourceRequest,
      success: SourceRef,
      error: [BadRequest, InternalServerError],
    }).middleware(Authorization),
    'Accept an n8n source',
    'Creates a source reference from an n8n webhook payload for a later workflow run.',
  ),
) {}

export class SelfHostLegacyDocumentApi extends HttpApiGroup.make('legacy-documents').add(
  documented(
    HttpApiEndpoint.get('status', '/document/:id/status', {
      params: DocumentIdParams,
      success: SelfHostJsonObject,
      error: NotFound,
    }),
    'Read legacy document status',
    'Compatibility projection for clients using the pre-v1 document grammar.',
  ),
  documented(
    HttpApiEndpoint.get('markdown', '/document/:id/full.md', {
      params: DocumentIdParams,
      success: Schema.String.pipe(
        HttpApiSchema.asText({ contentType: 'text/markdown; charset=utf-8' }),
      ),
      error: NotFound,
    }),
    'Read legacy document Markdown',
    'Compatibility Markdown projection for the pre-v1 document grammar.',
  ),
  documented(
    HttpApiEndpoint.get('pages', '/document/:id/pages', {
      params: DocumentIdParams,
      success: Schema.Array(SelfHostJsonObject),
      error: NotFound,
    }),
    'List legacy document pages',
    'Compatibility page projection for the pre-v1 document grammar.',
  ),
  documented(
    HttpApiEndpoint.get('page', '/document/:id/page/:page', {
      params: DocumentPageParams,
      success: SelfHostJsonObject,
      error: NotFound,
    }),
    'Read one legacy document page',
    'Returns one one-based page projection from the normalized graph.',
  ),
  documented(
    HttpApiEndpoint.get('nodes', '/document/:id/nodes', {
      params: DocumentIdParams,
      success: SelfHostJsonObject,
      error: NotFound,
    }),
    'List legacy document nodes',
    'Compatibility node projection for the pre-v1 document grammar.',
  ),
  documented(
    HttpApiEndpoint.get('download', '/document/:id/download', {
      params: DocumentIdParams,
      success: Schema.Uint8Array.pipe(
        HttpApiSchema.asUint8Array({ contentType: 'application/pdf' }),
      ),
      error: NotFound,
    }),
    'Download the original PDF',
    'Returns the locally stored source PDF when the source was uploaded as bytes.',
  ),
  documented(
    HttpApiEndpoint.post('uploadUrl', '/document/:id/upload-url', {
      params: DocumentIdParams,
      payload: DocumentUploadUrlRequest,
      success: DocumentUploadResult,
      error: [BadRequest, InternalServerError],
    }).middleware(Authorization),
    'Register a PDF URL',
    'Compatibility upload route that registers a remote PDF source and initializes its graph.',
  ),
  documented(
    HttpApiEndpoint.post('upload', '/document/:id/upload', {
      params: DocumentIdParams,
      headers: {
        'content-type': Schema.optionalKey(Schema.String),
        'x-file-name': Schema.optionalKey(Schema.String),
      },
      payload: Schema.Uint8Array.pipe(
        HttpApiSchema.asUint8Array({ contentType: 'application/pdf' }),
      ),
      success: DocumentUploadResult,
      error: [BadRequest, InternalServerError],
    }).middleware(Authorization),
    'Upload a PDF',
    'Compatibility upload route that stores PDF bytes and initializes the normalized document graph.',
  ),
) {}

/** The executable API reuses the public groups so runtime and OpenAPI cannot drift. */
export class SelfHostCoreApi extends HttpApi.make('okra-self-host-core-api')
  .add(SelfHostSystemApi)
  .add(SelfHostRuntimeApi)
  .add(SelfHostWorkflowCatalogApi)
  .add(SelfHostDocumentApi)
  .add(SelfHostSourceApi)
  .add(SelfHostLegacyDocumentApi)
  .add(SelfHostWorkflowApi) {}

export class SelfHostApi extends HttpApi.make('okra-self-host-api')
  .add(SelfHostSystemApi)
  .add(SelfHostRuntimeApi)
  .add(SelfHostWorkflowCatalogApi)
  .add(SelfHostWorkflowApi)
  .add(SelfHostDocumentApi)
  .add(SelfHostSourceApi)
  .add(SelfHostLegacyDocumentApi)
  .annotateMerge(
    OpenApi.annotations({
      title: 'okraPDF Self-Host API',
      version: '0.1.0',
      description:
        'Effect-native API contract for self-hosted document graphs and hybrid OCR/VLM workflows.',
    }),
  ) {}

const selfHostOpenApiSpec = OpenApi.fromApi(SelfHostApi);

export class SelfHostApiError extends Data.TaggedError('SelfHostApiError')<{
  readonly message: string;
  readonly method: string;
  readonly cause?: unknown;
}> {}

const RegistryTypeId = 'okrapdf/cli/SelfHostApiRegistry/TypeId' as const;

export interface SelfHostApiRegistry {
  readonly [RegistryTypeId]: typeof RegistryTypeId;
  readonly getOpenApiSpec: () => Effect.Effect<OpenApi.OpenAPISpec, SelfHostApiError>;
  readonly transportRoutes: () => Effect.Effect<
    ReadonlyArray<SelfHostApiTransportRoute>,
    SelfHostApiError
  >;
}

export const SelfHostApiRegistry: Context.Service<SelfHostApiRegistry, SelfHostApiRegistry> =
  Context.Service('okrapdf/cli/SelfHostApiRegistry');

export function getSelfHostOpenApiSpec(): OpenApi.OpenAPISpec {
  return selfHostOpenApiSpec;
}

export interface SelfHostApiTransportRoute {
  readonly method: string;
  readonly path: string;
}

const HTTP_METHODS = new Set(['get', 'post', 'put', 'patch', 'delete', 'head', 'options']);

/**
 * Derive transport registrations from the Effect-generated OpenAPI document.
 * Hono remains the Node/static adapter, but it cannot acquire a second route
 * list that drifts from the application contract.
 */
export function selfHostApiTransportRoutes(): ReadonlyArray<SelfHostApiTransportRoute> {
  const routes: SelfHostApiTransportRoute[] = [];
  for (const [openApiPath, operations] of Object.entries(selfHostOpenApiSpec.paths)) {
    for (const method of Object.keys(operations ?? {})) {
      if (!HTTP_METHODS.has(method.toLowerCase())) continue;
      routes.push({
        method: method.toUpperCase(),
        path: openApiPath.replace(/\{([^}]+)\}/g, ':$1'),
      });
    }
  }
  return routes;
}

export function selfHostApiRouteSignatures(): ReadonlySet<string> {
  return new Set(
    selfHostApiTransportRoutes().map(
      ({ method, path }) => `${method} ${path.replace(/:[A-Za-z0-9_]+/g, ':p')}`,
    ),
  );
}

export function makeSelfHostApiRegistry(): SelfHostApiRegistry {
  return SelfHostApiRegistry.of({
    [RegistryTypeId]: RegistryTypeId,
    getOpenApiSpec: () =>
      Effect.try({
        try: () => selfHostOpenApiSpec,
        catch: (cause) =>
          new SelfHostApiError({
            method: 'getOpenApiSpec',
            message: 'SelfHostApi.getOpenApiSpec failed',
            cause,
          }),
      }),
    transportRoutes: () =>
      Effect.try({
        try: () => selfHostApiTransportRoutes(),
        catch: (cause) =>
          new SelfHostApiError({
            method: 'transportRoutes',
            message: 'SelfHostApi.transportRoutes failed',
            cause,
          }),
      }),
  });
}

export const layer: Layer.Layer<SelfHostApiRegistry> = Layer.succeed(
  SelfHostApiRegistry,
  makeSelfHostApiRegistry(),
);

export const layerMemory: Layer.Layer<SelfHostApiRegistry> = layer;
